import React from "react";
import { withRouter } from "react-router-dom";
import { trackError } from "../utils/healthReporter";
import { clearLocalStorageKeeping } from "../utils/tillDeviceRegistration";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      unhandledRejection: false,
      errorMessage: "",
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || "Unknown error" };
  }

  componentDidMount() {
    window.addEventListener("unhandledrejection", this.handlePromiseRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("unhandledrejection", this.handlePromiseRejection);
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error:", error);
    console.error("Error Info:", errorInfo);
    trackError();

    try {
      if (window.electronAPI?.observability?.log) {
        window.electronAPI.observability.log("error", "render", error?.message || "React render error", {
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
          componentStack: errorInfo?.componentStack,
        });
      }
    } catch (e) {
      console.error("Failed to log error to observability:", e);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // No-op: removed auto-re-trigger of error state that caused infinite error modal loop
  }

  handlePromiseRejection = (event) => {
    console.error("Unhandled promise rejection:", event.reason);
    trackError();
    // Don't show error modal for unhandled promise rejections — they are non-fatal
    // and should not block the POS UI. Only componentDidCatch (render errors) should be fatal.

    // Log to observability (non-fatal)
    if (window.electronAPI?.observability?.log) {
      const reason = event.reason;
      window.electronAPI.observability.log("warn", "render", reason?.message || "Unhandled promise rejection", {
        name: reason?.name,
        message: reason?.message,
        stack: reason?.stack,
      });
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    try {
      // The local tables stay, so this browser keeps its device id (BUG-2621).
      clearLocalStorageKeeping(["authTokens", "oAuthData"]);
    } catch (e) {
      console.error("Failed to clear localStorage:", e);
    }
    window.location.href = "/till";
  };

  handleGoToLogin = () => {
    try { clearLocalStorageKeeping(); } catch (e) { /* ignore */ }
    window.location.href = "/sign-in";
  };

  render() {
    const { hasError, unhandledRejection, errorMessage } = this.state;

    if (hasError || unhandledRejection) {
      return (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999, fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif" }}>
          <div style={{ backgroundColor: "#fff", borderRadius: "12px", padding: "32px", width: "480px", maxWidth: "90vw", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
              <div style={{ width: "48px", height: "48px", backgroundColor: "#FEE4E2", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>
                &#9888;
              </div>
            </div>

            <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#101828", marginBottom: "8px", textAlign: "center" }}>
              POS System Error
            </h2>

            <p style={{ fontSize: "14px", color: "#475467", marginBottom: "16px", textAlign: "center" }}>
              The POS encountered an error and needs to be reloaded.
            </p>

            {errorMessage && (
              <div style={{ backgroundColor: "#FEF3F2", border: "1px solid #FEE4E2", borderRadius: "6px", padding: "8px 12px", fontSize: "12px", color: "#B42318", marginBottom: "16px", wordBreak: "break-word", maxHeight: "60px", overflow: "auto" }}>
                {errorMessage}
              </div>
            )}

            <div style={{ backgroundColor: "#F9FAFB", borderRadius: "8px", padding: "12px 16px", fontSize: "12px", color: "#475467", marginBottom: "24px" }}>
              <strong>Try these steps in order:</strong>
              <ol style={{ margin: "8px 0 0 0", paddingLeft: "18px" }}>
                <li>Click <strong>"Reload POS"</strong> to refresh</li>
                <li>If it happens again, click <strong>"Clear Cache & Reload"</strong></li>
                <li>If still stuck, click <strong>"Go to Login"</strong> to restart your session</li>
              </ol>
            </div>

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={this.handleGoToLogin} style={{ backgroundColor: "#fff", border: "1px solid #D0D5DD", color: "#344054", padding: "8px 14px", fontSize: "13px", fontWeight: 500, borderRadius: "8px", cursor: "pointer" }}>
                Go to Login
              </button>
              <button onClick={this.handleClearAndReload} style={{ backgroundColor: "#FEF3F2", border: "1px solid #FEE4E2", color: "#B42318", padding: "8px 14px", fontSize: "13px", fontWeight: 500, borderRadius: "8px", cursor: "pointer" }}>
                Clear Cache & Reload
              </button>
              <button onClick={this.handleReload} style={{ backgroundColor: "#2970FF", border: "none", color: "#fff", padding: "8px 14px", fontSize: "13px", fontWeight: 500, borderRadius: "8px", cursor: "pointer" }}>
                Reload POS
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withRouter(ErrorBoundary);
